import { useCallback, useEffect, useRef, useState } from "react";

import { useInterval } from "usehooks-ts";

import {
  useAgentExecutionClient,
  useCopilotExecutionClient,
} from "src/api/agents/execution";
import type { Environment } from "src/clients/agent-management";
import { usePageVisibility } from "src/hooks/usePageVisibility";

const PREWARM_COOLDOWN_MS = 20_000; // dedup window in ms

// The agent runtime tears a session's container down after
// `IdleRuntimeSessionTimeout` (300 s on the agent-runner runtime), so a container
// warmed at page load is gone long before a user who works on the page for a
// while and only then asks the copilot something.
// Re-ping well inside that window, with room for one slow or dropped ping.
const KEEP_ALIVE_INTERVAL_MS = 120_000;

// A tab left open all day must not hold a container open all day. The agent
// runtime caps the session at `MaxLifetime` (8 h) regardless; this bounds the
// cost before then.
const KEEP_ALIVE_MAX_MS = 30 * 60_000;

// Tracks in-flight ping requests to prevent duplicates
const pingPromiseMap = new Map<string, Promise<void>>();

// Per-key cooldown timestamps
const lastPingTimestampMap = new Map<string, number>();

/**
 * Which invoke API the upcoming call will use. The ping has to go to the same
 * one: the two APIs sit behind different routes and the v2 one resolves the
 * copilot product from the agent id on its own.
 */
export type CopilotPrewarmApi = "copilots-v0" | "copilot-v2";

type UseCopilotPrewarmParams = {
  agentId: string | undefined;
  sessionId: string | null;
  enabled: boolean;
  api?: CopilotPrewarmApi;
  /** Only read by `copilots-v0`, whose access check is per environment. */
  environment?: Environment;
  /**
   * Keep pinging while the surface stays mounted, instead of warming once.
   * One ping only covers a user who asks something in the next few minutes.
   */
  keepAlive?: boolean;
};

/**
 * Pre-warms an agent runtime container for copilot invocations.
 * Sends a lightweight ping so the first actual invocation has lower latency.
 *
 * - Deduplicates via in-flight tracking and a 20 s cooldown window
 * - Fire-and-forget: errors are silently swallowed
 * - Auto-triggers on mount when `enabled` is true
 * - With `keepAlive`, re-pings on an interval while the tab is visible
 */
export const useCopilotPrewarm = ({
  agentId,
  sessionId,
  enabled,
  api = "copilots-v0",
  environment,
  keepAlive = false,
}: UseCopilotPrewarmParams): void => {
  const { client: agentClient } = useAgentExecutionClient();
  const { client: copilotClient } = useCopilotExecutionClient();

  const ping = useCallback(() => {
    if (!agentId || !sessionId) return;

    const sendPing =
      api === "copilot-v2"
        ? copilotClient &&
          (() =>
            copilotClient.pingCopilotCopilotApiV2PingPost({
              copilotPingRequest: { agent_id: agentId, session_id: sessionId },
            }))
        : agentClient &&
          (() =>
            agentClient.pingCopilotsManagementApiV0PingPost({
              pingRequest: {
                agent_id: agentId,
                session_id: sessionId,
                ...(environment === undefined ? {} : { environment }),
              },
            }));

    if (!sendPing) return;

    const key = `${agentId}:${sessionId}`;

    // Skip if ping already in-flight
    if (pingPromiseMap.has(key)) return;

    // Dedupe: skip if pinged within cooldown window
    const lastPing = lastPingTimestampMap.get(key) ?? 0;
    if (Date.now() - lastPing < PREWARM_COOLDOWN_MS) return;

    lastPingTimestampMap.set(key, Date.now());

    const pingPromise = sendPing()
      .catch(() => {}) // Swallow errors
      .finally(() => {
        if (pingPromiseMap.get(key) !== pingPromise) {
          // Outdated/stale promise, don't clean up the newer one
          return;
        }
        pingPromiseMap.delete(key);
      }) as unknown as Promise<void>;

    pingPromiseMap.set(key, pingPromise);
  }, [agentClient, copilotClient, api, agentId, sessionId, environment]);

  useEffect(() => {
    if (enabled) {
      ping();
    }
  }, [enabled, ping]);

  // Read through a ref so a new client or session identity does not restart the
  // schedule and hand the page a fresh `KEEP_ALIVE_MAX_MS` budget.
  const pingRef = useRef(ping);
  useEffect(() => {
    pingRef.current = ping;
  }, [ping]);

  const pageVisible = usePageVisibility();
  const [keepAliveExpired, setKeepAliveExpired] = useState(false);
  const keepAliveActive = enabled && keepAlive && !keepAliveExpired;

  // Fixed on the first active render, for the same reason `pingRef` exists.
  const keepAliveDeadlineRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (keepAliveActive) {
      keepAliveDeadlineRef.current ??= Date.now() + KEEP_ALIVE_MAX_MS;
    }
  }, [keepAliveActive]);

  // Every path that can ping has to consult the deadline, not just the
  // interval: hiding the tab parks the interval, so a tab that is never
  // visible for a full interval would otherwise keep pinging past the cap.
  const keepAlivePastDeadline = useCallback((): boolean => {
    if (Date.now() < (keepAliveDeadlineRef.current ?? Infinity)) return false;
    setKeepAliveExpired(true);
    return true;
  }, []);

  // Nobody is about to send a message from a hidden tab, and browsers throttle
  // its timers anyway, so a `null` delay parks the schedule while it is hidden.
  useInterval(
    () => {
      if (keepAlivePastDeadline()) return;
      pingRef.current();
    },
    keepAliveActive && pageVisible ? KEEP_ALIVE_INTERVAL_MS : null,
  );

  // Coming back to the tab is the point where the container has most likely
  // been reclaimed, and the parked interval has not run since it was hidden.
  const wasPageVisibleRef = useRef(pageVisible);
  useEffect(() => {
    const returnedToTab = pageVisible && !wasPageVisibleRef.current;
    wasPageVisibleRef.current = pageVisible;

    if (returnedToTab && keepAliveActive && !keepAlivePastDeadline()) {
      pingRef.current();
    }
  }, [keepAliveActive, keepAlivePastDeadline, pageVisible]);
};

/** Reset module-level state for testing purposes. */
export const __resetCopilotPrewarmState = (): void => {
  pingPromiseMap.clear();
  lastPingTimestampMap.clear();
};
